import { SOURCE_TYPES } from 'constants/source'
import {
  ADD_OFFER_LIST_EXTRA,
  API_CALL,
  API_CALL_FAILURE,
  API_CALL_REQUEST,
  API_CALL_SUCCESS,
  END_OFFER_LIST_STREAM,
  OFFER_SET_SEARCH_RESULT_METADATA,
  OFFER_SET_TOUR_SEARCH_RESULT_METADATA,
  OFFER_SHARE_COMPLETE,
  OFFER_SHARE_START,
  OFFER_UPDATE_AVAILABLE_RATES,
  OFFER_UPDATE_PRICING_DATA,
  OFFER_VIEWED,
  OFFERS_CLEAR,
  REMOVE_OFFER,
  SET_OFFER_LIST_BOUNDS,
  SET_OFFER_LIST_DESCRIPTION_LOADING,
  SET_OFFER_LIST_ERROR,
  SET_OFFER_LIST_SELECTED_FILTERS,
  SET_OFFER_LIST_SOCKET_STATUS,
  SET_OFFER_LIST_VALID_OFFER,
  UPDATE_OFFER_METADATA_DESCRIPTION,
} from './actionConstants'
import {
  FETCH_ALTERNATIVE_OFFERS,
  FETCH_AVAILABILITY_RATES_FOR_OFFER,
  FETCH_BEST_OFFER_FOR_PROPERTY,
  FETCH_BEST_PRICE_FOR_OFFER,
  FETCH_OFFER,
  FETCH_OFFER_FLIGHT_PRICE,
  FETCH_OFFER_LIST,
  FETCH_OFFER_LIST_FILTERS,
  FETCH_OFFER_LOCATION_BREADCRUMBS,
  FETCH_OFFER_META_DATA,
  FETCH_OFFER_TAGS,
  FETCH_OFFERS,
  FETCH_POPULAR_FILTERS,
  FETCH_PROPERTY_OFFER_MAPPING,
  FETCH_PROPERTY_TAGS,
  FETCH_RELATED_TRAVEL_ITEMS,
  FETCH_SURCHARGE_MARGIN,
  FETCH_TOUR_SEARCH_FACETS,
  FETCH_TRADER_INFORMATION,
} from './apiActionConstants'

import { SearchResultEntry, SeparatorResponse } from '@luxuryescapes/contract-search'
import {
  EndResponse,
  ErrorResponse,
  FilterResponse,
  OfferDescriptionLoadingResponse,
  OfferDescriptionResponse,
  OfferResponse,
  OfferValidatorResponse,
  SearchLocationResponse,
  SelectedFiltersResponse,
} from '@luxuryescapes/contract-search/types/sockets'
import { SHOULD_AUTOBATCH } from '@reduxjs/toolkit'
import { getDomainUserId } from 'analytics/snowplow/helpers/domainUserId'
import { getRatesForBedbankOffer } from 'api/bedbankRates'
import * as CalendarV2Service from 'api/calendarV2'
import { mapSearchResultToOfferListMetaData } from 'api/mappers/hotelOfferMap'
import * as OfferService from 'api/offer'
import { streamRequest } from 'api/offer'
import * as RecommendationService from 'api/recommendations'
import { occupancyToStr } from 'api/recommendations'
import { batchFetchSurchargeMargin } from 'api/reservation'
import * as SearchService from 'api/search'
import { getTagsByOfferId } from 'api/tags'
import { LabFeatureFlags } from 'constants/lab'
import {
  OFFER_TYPE_ALWAYS_ON,
  OFFER_TYPE_BED_BANK,
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_HOTEL_SLUG,
  OFFER_TYPE_TOUR,
  OFFER_TYPE_TOUR_SLUG,
  OFFER_TYPE_TOUR_V2,
  PRODUCT_TYPE_ULTRALUX_SLUG,
} from 'constants/offer'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import placesTree from 'constants/placesTree'
import { arrayToObject, groupBy, skip, sortBy, take, unique, uniqueBy } from 'lib/array/arrayUtils'
import { timeNowInSecond } from 'lib/datetime/time'
import { isOfferDisplayFlavorPayload } from 'lib/lab/dynamicPayload'
import getObjectKey from 'lib/object/getObjectKey'
import { objectEntries } from 'lib/object/objectUtils'
import { buildAvailableRateKey } from 'lib/offer/availabilityUtils'
import getOfferListKey from 'lib/offer/offerListKey'
import { isBedbankOffer, isBundleOffer, isCruiseOffer, isLEOffer, isTourV1Offer } from 'lib/offer/offerTypes'
import {
  addSearchSocketListener,
  addStateListener,
  getStateListeners, removeAllSearchSocketListenersAndSafelyDisconnect,
  removeStateListenerAndMaybePause,
} from 'lib/search/searchSocketListeners'
import {
  buildDestinationSearchParamsKey,
  buildSearchParamsFromFilters,
  buildSearchParamsKey,
  buildSuggestedDatesParamsKey,
  getOccupancyFromSearchStrings,
  updateLeBrandSpecificFilters,
} from 'lib/search/searchUtils'
import { nameToSearchFriendlyQueryParamsTransform, urlTransform } from 'lib/string/removeSpaceUtils'
import uuidV4 from 'lib/string/uuidV4Utils'
import { getCanViewLuxPlusBenefits, getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { getMetaOfferIds } from 'marketing/api'
import { checkBypassMode } from 'marketing/lib/lib'
import { emitSocketMessage, clearLastMessage } from 'lib/search/socketEmitter'
import { getCurrentUserId, selectRecDomainUserId } from 'selectors/accountSelectors'
import { getChannelMarkup } from 'selectors/channelMarkupSelector'
import { isSpoofed } from 'selectors/featuresSelectors'
import { getDefaultAirportCode } from 'selectors/flightsSelectors'
import { getOfferDeepLink } from 'selectors/offerDeepLinkSelector'
import { getSource, isPaidSession } from 'selectors/offerSelectors'
import invariant from 'tiny-invariant'
import { convertBedbankOfferToSummary } from './BedbankOfferActions'
import { AppAction } from './types/ActionTypes'
import config from 'constants/config'

export interface PricingData {
  price: number
  value: number
  taxesAndFees: number
  propertyFees: number
  memberPrice?: number
}

export function offerShareButtonClick(pageId: string, pageType: string): AppAction {
  return {
    type: OFFER_SHARE_START,
    data: {
      pageId,
      pageType,
    },
  }
}

export function offerShareComplete(pageId: string, pageType: string, shareMethod: string): AppAction {
  return {
    type: OFFER_SHARE_COMPLETE,
    data: {
      pageId,
      pageType,
      shareMethod,
    },
  }
}

export function fetchAccumulatedOfferById(
  offerId: string,
  regionCode?: string,
  flightOrigin?: string,
): AppAction {
  return (dispatch, getState) => {
    const state: App.State = getState()
    if (state.offer.offerErrors[offerId] || state.offer.offersLoading[offerId] || state.offer.offers[offerId]) {
      return
    }
    const channelMarkup = getChannelMarkup(state)
    const offerDeepLink = getOfferDeepLink(state)

    const isSpoofed = state?.auth?.account.isSpoofed

    // Include flavor query param if enableOfferDisplayFlavor flag is true, with its value set to the value of the flag
    const enableOfferDisplayFlavor = state.system.features.labFlags[LabFeatureFlags.enableOfferDisplayFlavor]

    const flavor = enableOfferDisplayFlavor?.enabled && isOfferDisplayFlavorPayload(enableOfferDisplayFlavor.value)
      ? enableOfferDisplayFlavor.value.flavor
      : undefined

    dispatch({
      type: API_CALL,
      api: FETCH_OFFER,
      meta: {
        [SHOULD_AUTOBATCH]: true,
      },
      request: async() => {
        const newOfferId = await OfferService.getOfferIdBySlug(offerId).catch(() => undefined)
        return OfferService.getAccumulatedOfferById(
          newOfferId ?? offerId,
          regionCode || state.geo.currentRegionCode,
          flightOrigin || getDefaultAirportCode(state),
          state.geo.currentCurrency,
          isSpoofed,
          channelMarkup,
          offerDeepLink,
          flavor,
        )
      },
      offerId,
    })
  }
}

interface FetchOneOfferOptions { regionCode?: string, flightOrigin?: string, allPackages?: boolean, privateRequestKey?: string }

export function fetchOfferById(offerId: string, {
  regionCode,
  flightOrigin,
  allPackages = false,
  privateRequestKey,
}: FetchOneOfferOptions = {}): AppAction {
  return (dispatch, getState) => {
    const state: App.State = getState()
    const isSpoofed = state.auth.account.isSpoofed

    const offerKey = privateRequestKey ? `${offerId}-${privateRequestKey}` : offerId

    if (state.offer.offerErrors[offerKey] || state.offer.offersLoading[offerKey] || state.offer.offers[offerKey]) {
      return
    }
    const channelMarkup = getChannelMarkup(state)

    // TODO: look into how to extract lab flag payload value
    // Include flavor query param only when flag is enabled and valid payload
    const enableOfferDisplayFlavor = state.system.features.labFlags[LabFeatureFlags.enableOfferDisplayFlavor]
    const flavor = enableOfferDisplayFlavor?.enabled && isOfferDisplayFlavorPayload(enableOfferDisplayFlavor.value)
      ? enableOfferDisplayFlavor.value.flavor
      : undefined

    dispatch({
      type: API_CALL,
      api: FETCH_OFFER,
      meta: {
        [SHOULD_AUTOBATCH]: true,
      },
      request: async() => {
        const newOfferId = await OfferService.getOfferIdBySlug(offerId).catch(() => undefined)

        return OfferService.getOfferById(newOfferId ?? offerId, {
          region: regionCode || state.geo.currentRegionCode,
          flightOrigin: flightOrigin || getDefaultAirportCode(state),
          allPackages,
          privateRequestKey,
          currentCurrency: state.geo.currentCurrency,
          isSpoofed,
          channelMarkup,
          offerDeepLink: getOfferDeepLink(state),
          flavor,
        })
      },
      offerId: offerKey,
    })
  }
}

export function fetchOfferFlightPrice(offerId: string, flightOrigin: string): AppAction {
  return (dispatch, getState) => {
    const state: App.State = getState()

    const existingOffer = state.offer.offers[offerId]
    if (existingOffer?.flightPrices[flightOrigin] || !existingOffer) {
      // already have price, no need to re-load
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_OFFER_FLIGHT_PRICE,
      request: () => OfferService.getOfferFlightPrice({
        flightOrigin,
        flightDestination: existingOffer.flightDestinationPort,
        currency: state.geo.currentCurrency,
        region: state.geo.currentRegionCode,
        duration: existingOffer.lowestPricePackage?.duration ?? 1,
        forceBundleId: existingOffer.forceBundleId,
        travelToDate: existingOffer.travelToDate,
      }),
      offerId,
      flightOrigin,
    })
  }
}

function convertLEOfferToSummary(offer: App.Offer): App.OfferSummary {
  const {
    packages,
    ...offerSummary
  } = offer
  return {
    ...offerSummary,
    // offer summaries have the field present, so it can work in tandem with an actual offer
    // but the packages will always be empty to save space
    packages: [],
  }
}

export function convertOfferToSummary(
  offer: App.Offer | App.BedbankOffer,
): App.OfferSummary | App.BedbankOfferSummary {
  if (isBedbankOffer(offer)) {
    return convertBedbankOfferToSummary(offer)
  } else if (isBundleOffer(offer) || isCruiseOffer(offer)) {
    // some offer type doesn't know how to summary yet
    return offer
  } else {
    return convertLEOfferToSummary(offer)
  }
}

type FetchOfferOptions = {
  regionCode?: string
  includePackages?: boolean
}

/**
 * Bulk fetch of offers
 *
 * @remarks
 * Before requesting the fetch,
 * Checks are made against offers currently being loaded and offers that are missing data
 *
 * This ensures we only fetch when absolutely necessary
 */
export function fetchOffersById(
  offerIds: Array<string> = [],
  options: FetchOfferOptions = {},
): AppAction {
  return (dispatch, getState) => {
    const state: App.State = getState()
    const { includePackages, regionCode } = options
    const missingOfferIds = unique(offerIds.filter(function(id) {
      if (
        // don't have the offer at all and it's not currently being loaded
        (!state.offer.offers[id] && !state.offer.offersLoading[id] && !state.offer.offerErrors[id]) ||
        // needs packages but is missing them, we have to refetch
        (includePackages && state.offer.offers[id]?.packages.length === 0)
      ) {
        return true
      }
    }))

    if (missingOfferIds.length === 0) {
      // already have all the offers
      return
    }

    const authState: App.AuthState = state.auth
    const channelMarkup = getChannelMarkup(state)
    const offerDeepLink = getOfferDeepLink(state)

    // TODO: look into how to extract lab flag payload value
    // Include flavor query param only when flag is enabled and valid payload
    const enableOfferDisplayFlavor = state.system.features.labFlags[LabFeatureFlags.enableOfferDisplayFlavor]
    const flavor = enableOfferDisplayFlavor?.enabled && isOfferDisplayFlavorPayload(enableOfferDisplayFlavor.value)
      ? enableOfferDisplayFlavor.value.flavor
      : undefined

    const fetchOptions = {
      ...options,
      flightOrigin: getDefaultAirportCode(state),
      currentCurrency: state.geo.currentCurrency,
      isSpoofed: authState.account.isSpoofed,
      enablePropertyCollect: state.system.features.flags[OptimizelyFeatureFlags.croPayAtProperty],
      flavor,
    }

    dispatch({
      type: API_CALL,
      api: FETCH_OFFERS,
      request: () => OfferService.getOffersById(missingOfferIds, regionCode || state.geo.currentRegionCode, fetchOptions, channelMarkup, offerDeepLink),
      offerIds: missingOfferIds,
    })
  }
}

interface Options {
  clientCheckAvailability?: boolean
  typeaheadId?: string
}

export function fetchOfferList(filters: App.OfferListFilters = {}, options: Options = {}): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const canViewLuxPlusBenefits = getCanViewLuxPlusBenefits(state)
    const luxPlusEnabled = getIsLuxPlusEnabled(state)
    const isPaidSessionEnabled = isPaidSession(state)
    const channelMarkup = getChannelMarkup(state)
    const offerListKey = getOfferListKey(filters)

    if (state.offer.offerLists[offerListKey]) {
      // already have the results for this set of params
      return
    }

    if (filters.checkIn && !filters.checkOut) {
      // This request is only valid if both checking and checkout dates are provided
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_OFFER_LIST,
      request: () => OfferService.getOfferList({
        region: state.geo.currentRegionCode,
        filters,
        canViewLuxPlusBenefits,
        isLuxPlusEnabled: luxPlusEnabled,
        clientCheckAvailability: options.clientCheckAvailability,
        isPaidSession: isPaidSessionEnabled,
        isSpoofed: isSpoofed(state),
        accessToken: state.auth.accessToken,
        channelMarkup,
      }).then((results) => {
        let metaData: Array<App.OfferListMetaData> = results.metaData || []
        const offerIds: Array<string> = results.offerIds
        // Finding all bundle offers and creating a map object
        const mapBundledOffers = arrayToObject(
          metaData.filter(m => m.bundledOfferId && m.available),
          r => r.bundledOfferId!,
          r => r.offerId,
        )

        // Adding for single offers property bundleOfferId
        metaData = metaData.map(r => mapBundledOffers[r.offerId] ? {
          ...r,
          bundleOfferId: mapBundledOffers[r.offerId],
        } : r)

        if (metaData.length > 0 && (filters.landmarkId || filters.destinationId || filters.propertyId || filters.bounds)) {
          dispatch(setSearchResultMetadata(metaData, filters))
        }

        const tourMetadata = results.tourMetadata || []
        if ((tourMetadata.length > 0) && (filters.destinationId)) {
          dispatch(setTourSearchResultMetadata(tourMetadata, filters))
        }

        if (results.filters) {
          const filters = updateLeBrandSpecificFilters(results.filters) as App.OfferListAvailableFilters
          const filterOrder = updateLeBrandSpecificFilters(results.filterOrder) ?? { amenities: {}, holidayTypes: {}, locations: {} }
          const offerListFilters: Partial<App.OfferListFilterOptions> = {
            filters,
            filterOrder,
            orderedFilters: {
              holidayTypes: sortBy(
                objectEntries(filters.holidayTypes),
                ([_, count]) => count,
                'desc',
              ).map(([value, count]) => ({ value, count })),
              amenities: sortBy(
                objectEntries(filters.amenities),
                ([_, count]) => count,
                'desc',
              ).map(([value, count]) => ({ value, count })),
            },
          }
          dispatch({
            type: API_CALL_SUCCESS,
            api: FETCH_OFFER_LIST_FILTERS,
            data: offerListFilters,
            key: offerListKey,
          })
        }

        return {
          searchId: results.searchId,
          mainOfferList: unique(filters.limit ? take(offerIds, filters.limit) : offerIds),
          offerCount: results.offerCount,
          searchVertical: results.searchVertical,
        }
      }),
      key: offerListKey,
    })
  }
}

export function fetchPersonalisedOfferList(filters: App.OfferListFilters): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const offerListKey = getOfferListKey(filters)

    if (state.offer.offerLists[offerListKey]) {
      // already have the results for this set of params
      return
    }

    // Include these fields in the filters to ensure data is only refetched when a new combination is selected, fallback to redux state if not provided
    const {
      leUserId,
      isLuxPlus = getCanViewLuxPlusBenefits(state),
      baseOfferId: offerId,
      minMpv,
      checkIn,
      checkOut,
      rooms,
      offerTypes,
      rankerEnabled,
    } = filters
    const region = state.geo.currentRegionCode
    const domainUserId = selectRecDomainUserId(state)

    let occupancy: string | undefined = undefined
    if (rooms) {
      occupancy = occupancyToStr(rooms)
    }

    const mapRecommendationsToMainOfferList = ({ offers }: { offers: Array<App.RecommendationOffer> | Array<App.Recommendation> }) => ({
      mainOfferList: offers.map(o => o.offerId),
      offerCount: offers.length,
    })
    // Work around for conditional dispatch
    let request = () => new Promise((resolve) => resolve({ offers: [] })).then(mapRecommendationsToMainOfferList)
    if (filters.lere === 'alternatives' && (domainUserId || leUserId)) {
      request = () => RecommendationService.getAlternativesRecommendations(region, isLuxPlus, leUserId, domainUserId).then(mapRecommendationsToMainOfferList)
    } else if (filters.lere === 'peopleLikeMe' && leUserId) {
      request = () => RecommendationService.getPeopleLikeMeRecommendations(leUserId, region, isLuxPlus).then(mapRecommendationsToMainOfferList)
    } else if (filters.lere === 'graph' && offerId) {
      request = () => RecommendationService.getRecommendations(offerId, region, isLuxPlus, false, checkIn, checkOut, occupancy, minMpv).then(mapRecommendationsToMainOfferList)
    } else if (filters.lere === 'graph-metasearch' && offerId) {
      request = () => RecommendationService.getRecommendations(offerId, region, isLuxPlus, false, checkIn, checkOut, occupancy, minMpv, true).then(mapRecommendationsToMainOfferList)
    } else if (filters.lere === 'twoTowers' && (offerId || leUserId || domainUserId)) {
      request = () => RecommendationService.getTwoTowersRecommendations({
        leUserId,
        domainUserId,
        offerId,
        region,
        isLuxPlusMember: isLuxPlus,
        checkIn,
        checkOut,
        occupancy,
        offerTypes,
        rankerEnabled,
      }).then(mapRecommendationsToMainOfferList)
    }

    dispatch({
      type: API_CALL,
      api: FETCH_OFFER_LIST,
      request,
      key: offerListKey,
    })
  }
}

export function fetchOfferListFilters(filters: App.OfferListFilters): AppAction {
  // filters come with the offer list in search now
  return fetchOfferList(filters)
}

export function fetchTourSearchFacets(filters: App.OfferListFilters = {}): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const destinationId = filters.destinationId
    const excludeIds = filters.offerIdsToExclude?.join(',')

    const params = {
      placeId: destinationId,
      region: state.geo.currentRegionCode,
      category: filters.categories?.[0],
      luxPlusExclusive: filters.luxPlusFeatures,
      agentHubExclusive: filters.agentHubFeatures,
      priceGte: filters.priceGte ? parseFloat(filters.priceGte) : undefined,
      priceLte: filters.priceLte ? parseFloat(filters.priceLte) : undefined,
      onSale: filters.onSale,
      flashOffer: filters.flashOffer,
      tourLengthGte: filters.tourLengthGte ? parseFloat(filters.tourLengthGte) : undefined,
      tourLengthLte: filters.tourLengthLte ? parseFloat(filters.tourLengthLte) : undefined,
      tourStyle: filters.tourStyles?.[0],
    }

    const key = getObjectKey(params)
    if (state.tour.tourFacets[key] && !excludeIds) {
      // already have the results for this set of params
      return
    }
    if ((filters.offerTypes?.includes(OFFER_TYPE_TOUR) || filters.offerTypes?.includes(OFFER_TYPE_TOUR_V2)) && destinationId) {
      dispatch({
        type: API_CALL,
        api: FETCH_TOUR_SEARCH_FACETS,
        request: () => SearchService.getTourFacets({ ...params, placeId: destinationId, excludeIds }),
        key,
      })
    }
  }
}

interface BestPriceParams {
  checkIn: string
  checkOut: string
  occupants: Array<App.Occupants>
  bundledOfferId?: string
}

export function fetchBestPriceForOffer(offer: App.HotelOffer | App.HotelOfferSummary | App.VillaOffer | App.VillaOfferSummary | App.TourOffer | App.TourOfferSummary | App.BundleOffer, params: BestPriceParams): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const key = buildSearchParamsKey(params.checkIn, params.checkOut, params.occupants, params.bundledOfferId)
    const offerId = offer.id
    const channelMarkup = getChannelMarkup(state)

    if (
      state.offer.offerBestPrices[offerId]?.[key] ||
      state.offer.offerPricesLoading[offerId]?.[key] ||
      state.offer.offerPricesErrors[offerId]?.[key]
    ) {
      // Already have details, or are loading them, or encountered an error while loading them
      return
    }

    // occupants we're given don't transform children/infant categories
    // based on property, so we need to do it here
    const rooms = params.occupants.map(occupants =>
      getOccupancyFromSearchStrings(
        occupants.adults,
        occupants.childrenAge,
        offer.property?.maxChildAge,
        offer.property?.maxInfantAge,
        offer.property?.maxTeenagerAge,
      ),
    )

    dispatch({
      key,
      offerId,
      type: API_CALL,
      api: FETCH_BEST_PRICE_FOR_OFFER,
      request: () => CalendarV2Service.getLowestPriceForOffer({
        offerType: offer.type,
        offerIds: [offerId],
        regionCode: state.geo.currentRegionCode,
        currencyCode: state.geo.currentCurrency,
        timezone: offer.property?.timezone ?? 'UTC',
        checkIn: params.checkIn,
        checkOut: params.checkOut,
        rooms,
        bundledOfferId: params.bundledOfferId,
        saleUnit: offer.saleUnit,
        channelMarkup,
      }),
    })
  }
}

export function fetchBestOfferForProperty(propertyId: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const [propertyType, id] = propertyId.split(':')

    if (state.offer.bestPropertyOffer[propertyId]) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_BEST_OFFER_FOR_PROPERTY,
      request: () => SearchService.getAlternativeOffers(
        id,
        propertyType === 'le' ? 'le' : 'bedbank',
        state.geo.currentRegionCode,
      ).then(result => {
        const leResultsByType = groupBy(result.leOfferDetails, res => res.type)

        // priority list of flash -> TAO -> LME -> bedbank
        const best = (
          leResultsByType.get(OFFER_TYPE_HOTEL) ??
          leResultsByType.get(OFFER_TYPE_ALWAYS_ON)
        )
        return {
          offerId: best?.[0].offerId ?? result.bedbank[0],
          type: best?.[0].offerId ? 'le' as const : 'bedbank' as const,
        }
      }),
      propertyId,
    })
  }
}

function filterByDefinedProperty<T, K extends keyof T>(arr: Array<T>, key: K): Array<T & { [key in K]: {} }> {
  function hasKey(el: T): el is T & { [key in K]: {} } {
    return !!el[key]
  }
  return arr.filter(hasKey)
}

function setSearchResultMetadata(
  metaData: Array<App.OfferListMetaData>,
  filters: App.OfferListFilters,
): AppAction {
  const searchTargetId = filters.landmarkId ?? filters.destinationId ?? filters.propertyId ?? ''
  const availabilityKey = buildDestinationSearchParamsKey(searchTargetId, filters.checkIn, filters.checkOut, filters.rooms)
  const suggestedDatesKey = buildSuggestedDatesParamsKey(filters.flexibleMonths, filters.flexibleNights, filters.rooms)
  const offerMetaDataKey = getOfferListKey(filters)

  return {
    type: OFFER_SET_SEARCH_RESULT_METADATA,
    data: {
      distanceFromSearchTarget: {
        [searchTargetId]: arrayToObject(filterByDefinedProperty(metaData, 'distance'), r => r.offerId, r => r.distance),
      },
      // all offers include unavailable ones
      offerAvailabilityFromSearchTarget: {
        [availabilityKey]: arrayToObject(filterByDefinedProperty(metaData, 'available'), r => r.offerId, r => r.available),
      },
      offerSuggestedDates: {
        [suggestedDatesKey]: arrayToObject(filterByDefinedProperty(metaData, 'suggestedTravelDates'), r => r.offerId, r => r.suggestedTravelDates),
      },
      offerMetaData: {
        [offerMetaDataKey]: arrayToObject(metaData, r => r.offerId, r => r),
      },
    },
    searchTargetId,
    availabilityKey,
    suggestedDatesKey,
    offerMetaDataKey,
  }
}

function setTourSearchResultMetadata(
  tourMetadata: Array<App.TourListMetadata>,
  filters: App.OfferListFilters,
): AppAction {
  const tourMetadataKey = getOfferListKey(filters)

  return {
    type: OFFER_SET_TOUR_SEARCH_RESULT_METADATA,
    data: {
      tourMetadata: {
        [tourMetadataKey]: arrayToObject(tourMetadata, r => r.tourId, r => r),
      },
    },
    tourMetadataKey,
  }
}

export interface AvailableRatesParams {
  checkIn: string;
  checkOut: string;
  occupants: Array<App.Occupants>;
  currencyCode?: string;
  flightOrigin?: string;
  bundleOfferId?: string;
  reservationId?: string;
  packageIds?: Array<string>;
}

export function fetchAvailableRatesForEachRoom(offer: App.Offer, params: AvailableRatesParams): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const offerId = offer.id
    const offerAvailableRatesByOccupancy = state.offer.offerAvailableRatesByOccupancy[offerId]
    const fetchedKeys = new Set(Object.keys(offerAvailableRatesByOccupancy || {}))

    params.occupants.map(occupants => {
      const key = buildAvailableRateKey(params.checkIn, params.checkOut, [occupants])
      if (!fetchedKeys.has(key)) {
        fetchedKeys.add(key)
        dispatch(fetchAvailableRates(offer, {
          ...params,
          occupants: [occupants],
        }))
      }
    })
  }
}

function updateAvailableRatesForOffer(offerId: string, key: string): AppAction {
  return {
    type: OFFER_UPDATE_AVAILABLE_RATES,
    offerId,
    key,
  }
}

export function fetchAvailableRatesForOffer(offer: App.Offer | App.OfferSummary | App.BundledOffer, params: AvailableRatesParams): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const offerId = offer.id
    const offerAvailableRates = state.offer.offerAvailableRatesByOccupancy[offerId]
    const currentOfferAvailableRates = state.offer.offerAvailableRates[offerId]

    const key = buildAvailableRateKey(params.checkIn, params.checkOut, params.occupants)

    if (params.packageIds) {
      // If `offerAvailableRates[key]` does not exist, then we're performing a partial load from
      // scratch. If it _does_ exist and its `packageIds` is `undefined`, that means we've already
      // kicked off a full load.
      const loadedPackageIds = offerAvailableRates?.[key] ? offerAvailableRates[key].packageIds : []
      if (loadedPackageIds == null) return

      const missingPackageIds = [...new Set(params.packageIds).difference(new Set(loadedPackageIds))]
      if (!missingPackageIds.length) return

      dispatch(fetchAvailableRates(offer, {
        ...params,
        packageIds: missingPackageIds,
      }))
    } else if (!offerAvailableRates || !(key in offerAvailableRates) || offerAvailableRates[key].packageIds) {
      // we have not fetched data with this key before, call the API
      dispatch(fetchAvailableRates(offer, params))
    } else if (!!currentOfferAvailableRates && !(key in currentOfferAvailableRates)) {
    // we did fetched it before but offerAvailableRates is not using data with that key
      dispatch(updateAvailableRatesForOffer(offerId, key))
    }
  }
}

function fetchAvailableRates(offer: App.Offer | App.OfferSummary | App.BundledOffer, params: AvailableRatesParams): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const offerId = offer.id
    const key = buildAvailableRateKey(params.checkIn, params.checkOut, params.occupants)
    const channelMarkup = getChannelMarkup(state)

    // occupants we're given don't transform children/infant categories
    // based on property, so we need to do it here
    const rooms = params.occupants.map(occupants =>
      getOccupancyFromSearchStrings(
        occupants.adults,
        occupants.childrenAge,
        offer.property?.maxChildAge,
        offer.property?.maxInfantAge,
        offer.property?.maxTeenagerAge,
      ),
    )

    dispatch({
      type: API_CALL,
      api: FETCH_AVAILABILITY_RATES_FOR_OFFER,
      request: () => CalendarV2Service.getAvailableRatesForOffer({
        offerType: offer.type,
        offerIds: [params.bundleOfferId ?? offerId],
        regionCode: state.geo.currentRegionCode,
        currencyCode: params.currencyCode || state.geo.currentCurrency,
        timezone: offer.property?.timezone ?? 'UTC',
        checkIn: params.checkIn,
        checkOut: params.checkOut,
        rooms,
        dynamic: true,
        flightOrigin: params.flightOrigin,
        bundledOfferId: params.bundleOfferId ? offerId : undefined,
        channelMarkup,
        reservationId: params?.reservationId,
        packageIds: params?.packageIds,
      }),
      offerId,
      key,
      packageIds: params?.packageIds,
    })
  }
}

function searchForLocation(location: string, place: App.Place): Array<App.Place> | undefined {
  if (place.name === location || place.canonicalName === location) {
    return [place]
  }
  if (place.children) {
    for (const child of place.children) {
      const result = searchForLocation(location, child)
      if (result !== undefined) {
        return [place, ...result]
      }
    }
  }
  return undefined
}

function getDeepestLocation(locations: Array<string>): Array<string> {
  let bestLocationPaths = [undefined] as Array<App.Place | undefined>
  for (const location of locations) {
    const locationPath = searchForLocation(location, { id: '', name: 'root', children: placesTree })
    if (locationPath !== undefined && locationPath.length > bestLocationPaths.length) {
      bestLocationPaths = locationPath
    }
  }

  return bestLocationPaths.length > 1 ? skip(bestLocationPaths, 1).map(path => path!.name) : take(locations, 1)
}

export function getRelatedTravelItemsForTour(offer: App.TourOffer): Array<App.BreadcrumbItem> {
  const locations = getDeepestLocation(offer.locations)
  const breadcrumbs: Array<App.BreadcrumbItem> = [
    { text: 'Tours', url: `/${OFFER_TYPE_TOUR_SLUG}` },
    ...locations.map(location => ({ text: location, url: `/${urlTransform(`${location}.Tours & Cruises`)}`.toLowerCase() })),
    { text: offer.lowestPricePackage.tour.name },
  ]
  return breadcrumbs
}

function breadcrumbURLConstructor(
  destination: Array<SearchService.Destination>,
  checkIn?: string,
  checkOut?: string,
  rooms?: Array<App.Occupants>,
) {
  const searchParams = buildSearchParamsFromFilters({
    checkIn,
    checkOut,
    rooms,
  })
  return `/search?destinationName=${nameToSearchFriendlyQueryParamsTransform(destination[0].name)}&destinationId=${destination[0].place_id}&${searchParams}`
}

export async function breadcrumbsFetcher(offer: App.Offer | App.BedbankOffer, state?: App.State, checkIn?: string, checkOut?: string, rooms?: Array<App.RoomOccupants>): Promise<Array<App.BreadcrumbItem>> {
  invariant(offer.property, 'Missing property on offer ' + offer.id)
  const leOffer = isLEOffer(offer)

  let firstItem = { text: 'Hotels', url: `/${OFFER_TYPE_HOTEL_SLUG}` }
  if (leOffer && offer.property.isUltraLux) {
    firstItem = { text: 'Ultra Lux', url: `/${PRODUCT_TYPE_ULTRALUX_SLUG}` }
  }

  let destinations: Array<SearchService.Destination>
  if (state?.offer.relatedTravelItems[offer.id]) {
    destinations = state.offer.relatedTravelItems[offer.id].map((destination) => {
      if (destination.placeId) {
        return { name: destination.text, place_id: destination.placeId }
      }
    }).filter((breadcrumb) => breadcrumb !== undefined) as Array<SearchService.Destination>
  } else {
    destinations = await SearchService.getBreadcrumbs(offer.property.id, (leOffer ? 'le' : 'bedbank'), false)
    destinations = destinations.reverse()
  }
  const locationPathDeduplicated = uniqueBy(destinations, d => d.name + '|' + d.place_id)
  const breadcrumbs = locationPathDeduplicated
    .map((location, index) => [location, ...locationPathDeduplicated.slice(0, index).reverse()])
    .map((locationPath) => ({
      text: locationPath[0].name,
      url: breadcrumbURLConstructor(locationPath, checkIn, checkOut, rooms),
      placeId: locationPath[0].place_id,
    }))

  let lastItem: App.BreadcrumbItem
  if (leOffer) {
    lastItem = {
      text: offer.property.name,
      url: `/offer/${offer.slug}/${offer.id}`,
    }
  } else {
    lastItem = {
      text: offer.name,
      url: `/partner/${offer.slug}/${offer.id}`,
    }
  }

  return [
    firstItem,
    ...breadcrumbs,
    lastItem,
  ]
}

export function fetchRelatedTravelItems(offer: App.Offer | App.BedbankOffer, checkIn?: string, checkOut?: string, rooms?: Array<App.RoomOccupants>): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (isTourV1Offer(offer)) {
      if (state.offer.relatedTravelItems[offer.id] !== undefined) {
        return
      }
      dispatch({
        type: API_CALL_SUCCESS,
        api: FETCH_RELATED_TRAVEL_ITEMS,
        offerId: offer.id,
        data: getRelatedTravelItemsForTour(offer),
      })
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_RELATED_TRAVEL_ITEMS,
      request: () => breadcrumbsFetcher(offer, state, checkIn, checkOut, rooms),
      offerId: offer.id,
    })
  }
}

export function fetchAlternativeOffers(propertyId: string, type: 'le' | 'bedbank', additionalKey: string = ''): AppAction {
  const key = [propertyId, additionalKey].join('')
  return (dispatch, getState) => {
    const state: App.State = getState()

    dispatch({
      type: API_CALL,
      api: FETCH_ALTERNATIVE_OFFERS,
      request: () => SearchService.getAlternativeOffers(propertyId, type, state.geo.currentRegionCode),
      key,
    })
  }
}

export function clearOffers(
  ids: {
    offerIds?: Array<string>
    bedbankOfferIds?: Array<string>
    tourV2OfferIds?: Array<string>
  },
): AppAction {
  return {
    type: OFFERS_CLEAR,
    ids,
  }
}

export function fetchTraderInformation(
  offerId: string,
  regionCode?: string,
): AppAction {
  return (dispatch, getState) => {
    const state: App.State = getState()

    if (state.offer.traderInformation[offerId]) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_TRADER_INFORMATION,
      request: () =>
        OfferService.getTraderInformation(
          offerId,
          regionCode || state.geo.currentRegionCode,
        ),
      offerId,
    })
  }
}

export function offerViewed(offerId: string, offerType: App.OfferType, isPriceMissing: boolean = false, allowWhiteLabels: boolean = false): AppAction {
  return (dispatch, getState) => {
    const newView: App.RecentlyViewedOffer = {
      offerId,
      offerType,
      creationTime: timeNowInSecond(), // store view time as sec
      category: 'recently_viewed',
      lereVersion: 'recently_viewed_local',
      isPriceMissing,
    }

    dispatch({
      type: OFFER_VIEWED,
      view: newView,
    })

    const state: App.State = getState()
    const userId = getCurrentUserId(state)
    const domainUserId = selectRecDomainUserId(state)

    const isLuxPlusMember = getCanViewLuxPlusBenefits(state)
    if ((userId || domainUserId) && !isSpoofed(state)) {
      RecommendationService.saveRecentlyViewedOffersWithDebounce([newView], state.geo.currentRegionCode, isLuxPlusMember, domainUserId, userId, allowWhiteLabels)
    }
  }
}

export function fetchOfferLocationBreadcrumbs(propertyId: string, type: 'le' | 'bedbank', showAllLocations: boolean): AppAction {
  return (dispatch, getState) => {
    const state: App.State = getState()

    if (state.offer.locationBreadcrumbs[propertyId]) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_OFFER_LOCATION_BREADCRUMBS,
      request: () => SearchService.getBreadcrumbs(propertyId, type, showAllLocations),
      propertyId,
    })
  }
}

export function updateHotelPackagePricingData(
  offerId: string,
  uniqueKey: string,
  data: Partial<PricingData>,
): AppAction {
  return {
    type: OFFER_UPDATE_PRICING_DATA,
    offerId,
    uniqueKey,
    data,
  }
}

export function fetchPopularHotelFilters(): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    dispatch({
      type: API_CALL,
      api: FETCH_POPULAR_FILTERS,
      request: () => SearchService.getPopularHotelFilters(state.geo.currentRegionCode),
    })
  }
}

function addOfferListExtra(offerListKey: string, extra: App.OfferListExtra): AppAction {
  return {
    type: ADD_OFFER_LIST_EXTRA,
    key: offerListKey,
    extra,
  }
}

function endOfferListStream(action: { offerListKey: string, searchId: string }): AppAction {
  const { offerListKey, searchId } = action

  return {
    type: END_OFFER_LIST_STREAM,
    key: offerListKey,
    searchId,
  }
}

export type StreamingHotelSearchOffersResponse = { result: Array<SearchResultEntry>, searchId: string }

export enum SEPARATOR_TYPE {
  NEARBY_OFFERS = 'NEARBY_OFFERS',
}

export function streamOfferList(
  filters: App.OfferListFilters = {},
  listenerId: string,
  minRequiredOffers?: number,
  newSearchId?: string,
  options: Options = {},
): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const { clientCheckAvailability, typeaheadId } = options
    const canViewLuxPlusBenefits = getCanViewLuxPlusBenefits(state)
    const luxPlusEnabled = getIsLuxPlusEnabled(state)
    const isPaidSessionEnabled = isPaidSession(state)
    const domainUserId = getDomainUserId()
    const memberId = state.auth.account.memberId
    const personalise = filters.shouldUsePersonalizedSearch
    const channelMarkup = getChannelMarkup(state)

    const offerListKey = getOfferListKey(filters)

    addStateListener({ eventGroupKey: offerListKey, listenerId })

    // If the stream has ended then we do not want to retry
    if (state.offer.offerLists[offerListKey]?.streamEnded ||
      state.offer.offerLists[offerListKey]?.socketListenersStatus === 'listening'
    ) {
      // already have the results for this set of params
      return
    }

    if (filters.checkIn && !filters.checkOut) {
      // This request is only valid if both checking and checkout dates are provided
      return
    }

    const searchId = state.offer.offerLists[offerListKey]?.searchId ?? newSearchId ?? uuidV4()
    // We only want to dispatch API_CALL_REQUEST when we are making a request for the first time
    if (!state.offer.offerLists[offerListKey]) {
      dispatch({
        type: API_CALL_REQUEST,
        api: FETCH_OFFER_LIST,
        key: offerListKey,
        searchId,
      })
    }

    dispatch({
      type: SET_OFFER_LIST_SOCKET_STATUS,
      key: offerListKey,
      status: 'listening',
    })

    const handleFiltersResponse = (response: FilterResponse) => {
      if (response.searchId !== searchId) return

      const offerListFilterResults = mapOfferListFiltersResponse(response)

      dispatch({
        type: API_CALL_SUCCESS,
        api: FETCH_OFFER_LIST_FILTERS,
        data: {
          ...getOfferListFilters(offerListFilterResults),
          offerCount: offerListFilterResults.offerCount,
        },
        key: offerListKey,
      })
    }

    const handleOfferResponse = (response: OfferResponse) => {
      if (response.searchId !== searchId) return

      const { offerId, metaData } = mapOfferListResponse(response, channelMarkup)

      if (metaData &&
        (
          filters.landmarkId
          || filters.destinationId
          || filters.propertyId
          || filters.bounds
          || (filters.lat && filters.lng)
          || filters.query
        )
      ) {
        dispatch(setSearchResultMetadata([metaData], filters))
      }

      dispatch({
        type: API_CALL_SUCCESS,
        api: FETCH_OFFER_LIST,
        key: offerListKey,
        data: {
          streamedOfferId: offerId,
        },
      })
    }

    const handleSeparatorResponse = (response: SeparatorResponse) => {
      if (response.searchId !== searchId) return

      const extra = mapSeparatorToExtra(response)

      if (extra) {
        dispatch(addOfferListExtra(offerListKey, extra))
      }
    }

    const handleEndResponse = (response: EndResponse) => {
      if (response.searchId !== searchId) return

      clearLastMessage(searchId)
      dispatch(endOfferListStream({ offerListKey, searchId }))
      dispatch({
        type: SET_OFFER_LIST_SOCKET_STATUS,
        key: offerListKey,
        status: 'not-listening',
      })
      removeAllSearchSocketListenersAndSafelyDisconnect({ eventGroupKey: offerListKey })
    }

    const handleErrorResponse = (errorResponse: ErrorResponse) => {
      // no clearLastMessage here as it will be called in handleEndResponse if needed
      let error = 'streaming error'

      if (errorResponse.type === 'searchNotFound') {
        if (searchId !== errorResponse.searchId) return
        handleEndResponse({ searchId })
        error = 'searchNotFound'
      } else if (errorResponse.type === 'validationError') {
        error = 'validationError'
      } else if (errorResponse.type === 'naturalLanguageSearch') {
        error = 'naturalLanguageSearchError'
        handleEndResponse({ searchId })
      }

      dispatch({
        type: API_CALL_FAILURE,
        api: FETCH_OFFER_LIST,
        key: offerListKey,
        error,
      })
    }

    const handleOfferValidatorResponse = (response: OfferValidatorResponse) => {
      const { searchId: responseSearchId, valid } = response

      if (responseSearchId === searchId) {
        dispatch({
          type: SET_OFFER_LIST_VALID_OFFER,
          key: offerListKey,
          valid,
        })
      }
    }

    const handleDisconnect = () => {
      dispatch({
        type: SET_OFFER_LIST_ERROR,
        key: offerListKey,
        error: 'disconnected',
        socketListenersStatus: 'not-listening',
      })
    }

    const handleConnect = () => {
      dispatch({
        type: SET_OFFER_LIST_ERROR,
        key: offerListKey,
        error: undefined,
      })
    }

    const handleLocationResponse = (response: SearchLocationResponse) => {
      const { searchId: responseSearchId, bounds } = response

      if (responseSearchId === searchId) {
        dispatch({
          type: SET_OFFER_LIST_BOUNDS,
          key: offerListKey,
          bounds,
        })
      }
    }

    const handleOfferDescriptionResponse = (response: OfferDescriptionResponse) => {
      const { searchId: responseSearchId, offerId, description } = response

      if (responseSearchId === searchId) {
        dispatch({
          type: UPDATE_OFFER_METADATA_DESCRIPTION,
          offerListKey: getOfferListKey(filters),
          offerId,
          description,
        })
      }
    }

    const handleOfferDescriptionLoadingResponse = (response: OfferDescriptionLoadingResponse) => {
      const { searchId: responseSearchId } = response

      if (responseSearchId === searchId) {
        dispatch({
          type: SET_OFFER_LIST_DESCRIPTION_LOADING,
          key: offerListKey,
        })
      }
    }

    const handleSelectedFiltersResponse = (response: SelectedFiltersResponse) => {
      const { searchId: responseSearchId, filters: selectedFilters } = response

      if (responseSearchId === searchId) {
        dispatch({
          type: SET_OFFER_LIST_SELECTED_FILTERS,
          key: getOfferListKey(filters),
          selectedFilters,
        })
      }
    }

    addSearchSocketListener({ eventGroupKey: offerListKey, event: 'search:response:filters', handler: handleFiltersResponse })
    addSearchSocketListener({ eventGroupKey: offerListKey, event: 'search:response:offer', handler: handleOfferResponse })
    addSearchSocketListener({ eventGroupKey: offerListKey, event: 'search:response:separator', handler: handleSeparatorResponse })
    addSearchSocketListener({ eventGroupKey: offerListKey, event: 'search:response:end', handler: handleEndResponse })
    addSearchSocketListener({ eventGroupKey: offerListKey, event: 'search:response:location', handler: handleLocationResponse })
    addSearchSocketListener({ eventGroupKey: offerListKey, event: 'search:response:error', handler: handleErrorResponse })
    addSearchSocketListener({ eventGroupKey: offerListKey, event: 'search:response:offer:validator', handler: handleOfferValidatorResponse })
    addSearchSocketListener({ eventGroupKey: offerListKey, event: 'search:response:offer:description:loading', handler: handleOfferDescriptionLoadingResponse })
    addSearchSocketListener({ eventGroupKey: offerListKey, event: 'search:response:offer:description', handler: handleOfferDescriptionResponse })
    addSearchSocketListener({ eventGroupKey: offerListKey, event: 'search:response:selected-filters', handler: handleSelectedFiltersResponse })
    addSearchSocketListener({ eventGroupKey: offerListKey, event: 'disconnect', handler: handleDisconnect })
    addSearchSocketListener({ eventGroupKey: offerListKey, event: 'connect', handler: handleConnect })

    const source = getSource(state)

    if (state.offer.offerLists[offerListKey]?.fetching !== false) {
      streamRequest({
        region: state.geo.currentRegionCode,
        filters,
        canViewLuxPlusBenefits,
        isLuxPlusEnabled: luxPlusEnabled,
        clientCheckAvailability,
        isPaidSession: isPaidSessionEnabled,
        memberId,
        domainUserId,
        searchId,
        minRequiredOffers,
        personalise,
        include: filters.include,
        publicRates: source.includes(SOURCE_TYPES.METASEARCH),
        typeaheadId,
      })
    }
  }
}

function mapOfferListResponse(
  response: OfferResponse,
  channelMarkup?: App.ChannelMarkup,
): { offerId: string, metaData?: App.OfferListMetaData } {
  const { result } = response
  return {
    offerId: result.id,
    metaData: mapSearchResultToOfferListMetaData(result, 0, channelMarkup),
  }
}

function mapSeparatorToExtra(
  response: SeparatorResponse,
): App.OfferListExtra | undefined {
  let type: App.OfferListExtraType

  switch (response.type) {
    case SEPARATOR_TYPE.NEARBY_OFFERS:
      type = 'nearby'
      break
    default:
      return
  }

  return {
    type,
    position: response.position,
  }
}

function mapOfferListFiltersResponse(response: FilterResponse): {
  filters: App.OfferListAvailableFilters
  filterOrder: App.OfferListFilterOrders
  offerCount: number
} {
  return {
    filters: {
      amenities: response.filters.amenities,
      bedrooms: response.filters.bedrooms,
      campaigns: response.filters.campaigns,
      customerRatings: response.filters.customerRatings,
      holidayTypes: response.filters.holidayTypes,
      inclusions: response.filters.inclusions,
      propertyTypes: response.filters.propertyTypes,
      offerTypes: response.filters.type,
      luxPlusFeatures: response.filters.luxPlusExclusive ?? {},
      loyalty: response.filters.societe ?? {},
      agentHubFeatures: response.filters.agentHubExclusive ?? {},
      paymentOptions: mapPaymentOptions(response.filters.paymentOptions ?? {}),
      chains: response.filters.chains ?? {},
      total: response.total,
    },
    filterOrder: response.filterOrder,
    offerCount: response.total,
  }
}

function getOfferListFilters(results: {
  filters: App.OfferListAvailableFilters
  filterOrder: App.OfferListFilterOrders
  offerCount: number
}): Omit<App.OfferListFilterOptions, 'error' | 'fetching' | 'searchId'> {
  const filters = updateLeBrandSpecificFilters(results.filters) as App.OfferListAvailableFilters
  const filterOrder = updateLeBrandSpecificFilters(results.filterOrder) ?? { amenities: {}, holidayTypes: {}, locations: {} }
  return {
    filters,
    filterOrder,
    orderedFilters: {
      holidayTypes: sortBy(
        objectEntries(filters.holidayTypes),
        ([_, count]) => count,
        'desc',
      ).map(([value, count]) => ({
        value,
        count,
      })),
      amenities: sortBy(
        objectEntries(filters.amenities),
        ([_, count]) => count,
        'desc',
      ).map(([value, count]) => ({
        value,
        count,
      })),
    },
  }
}

export function removeOffer(offerId: string): AppAction {
  return {
    type: REMOVE_OFFER,
    offerId,
  }
}

export function fetchCalculatedSurchargeMargin(
  roomRateId: string,
  surchargeMarginItem: App.ToBeCalculatedSurchargeMarginItem,
  surchargeMarginKey: string,
): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const currentSurchargeMargin = state.offer.surchargeMargins[surchargeMarginKey]

    if (!currentSurchargeMargin) {
      dispatch({
        type: API_CALL,
        api: FETCH_SURCHARGE_MARGIN,
        surchargeMarginKey,
        request: () => batchFetchSurchargeMargin([roomRateId, surchargeMarginItem]),
      })
    }
  }
}
export function fetchPropertyOfferMap(
  offerType: typeof OFFER_TYPE_ALWAYS_ON | typeof OFFER_TYPE_BED_BANK,
  offerId: string,
): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    // We don't expect Salesforce offer IDs and bedbank service property IDs to conflict.
    if (state.offer.propertyOfferMappings[offerId]) {
      // We've already fetched this record.
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_PROPERTY_OFFER_MAPPING,
      request: () => OfferService.getPropertyOfferMapping(offerType, offerId),
      offerId,
    })
  }
}

export function updateOfferListKey({ offerListKey, listenerId }: { offerListKey: string, listenerId: string }): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const offerList = state.offer.offerLists[offerListKey]
    removeStateListenerAndMaybePause({
      eventGroupKey: offerListKey,
      listenerId,
      searchId: offerList?.searchId,
      streamEnded: !!offerList?.streamEnded,
    })

    const offerListStateListeners = getStateListeners(offerListKey)

    if (offerListStateListeners.length < 1) {
      dispatch({
        type: SET_OFFER_LIST_SOCKET_STATUS,
        key: offerListKey,
        status: 'not-listening',
      })
    }
  }
}

export function getMinOffers({ offerListKey, minOffers }: { offerListKey: string, minOffers: number }): AppAction {
  return (_, getState) => {
    const state = getState()
    const offerList = state.offer.offerLists[offerListKey]
    const offerListFilters = state.offer.offerListFilterOptions[offerListKey]

    if (offerList?.socketListenersStatus === 'listening' &&
      offerListFilters &&
      !offerList?.streamEnded &&
      offerList?.searchId &&
      !offerList?.fetching &&
      offerList?.offerIds.length < minOffers
    ) {
      emitSocketMessage(
        offerList.searchId,
        'search:request:extend-to',
        { searchId: offerList.searchId, minOffers },
      )
    }
  }
}

interface FetchMetaSearchOfferParams {
  propertyId: string
  filters: App.OfferListFilters
  source: string
  utmBp?: string
  regionCode: string
  offerListKey: string
  alternativeSourceOfferListKey: string
}

export function fetchMetaSearchOfferRates(params: FetchMetaSearchOfferParams): AppAction {
  const { propertyId, filters, source, utmBp, regionCode, offerListKey, alternativeSourceOfferListKey } = params
  return (dispatch, getState) => {
    const state: App.State = getState()

    // Determine alternative source to check for caching
    let alternativeSource = source === 'metasearch' ? undefined : 'metasearch'
    if (checkBypassMode(utmBp) && !alternativeSource) {
      alternativeSource = 'loggedInMetasearch'
    }

    // Check if we already have the required metadata entries - if so, skip fetching
    const existingAlternativeMetaData = alternativeSource
      ? state.offer.searchResultMetadata.offerMetaData[alternativeSourceOfferListKey]?.[propertyId]
      : undefined
    const existingOriginalMetaData = state.offer.searchResultMetadata.offerMetaData[offerListKey]?.[propertyId]

    // If we have both metadata entries (or just original when no alternative source), no need to fetch
    const hasAlternativeData = alternativeSource ? !!existingAlternativeMetaData : true
    const hasOriginalData = !!existingOriginalMetaData

    if (hasAlternativeData && hasOriginalData) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_OFFER_META_DATA,
      offerId: propertyId,
      alternativeOfferMetaDataKey: alternativeSourceOfferListKey,
      originalOfferMetaDataKey: offerListKey,
      request: async() => {
        // Fetch bedbank rates for both original and alternative sources
        const [alternativeSourceBedbankRates, originalSourceBedbankRates] = await Promise.all([
          (filters.checkIn && filters.checkOut)
            ? getRatesForBedbankOffer({
                id: propertyId,
                checkIn: filters.checkIn,
                checkOut: filters.checkOut,
                region: regionCode,
                isSpoofed: false,
                source: alternativeSource,
                rooms: filters.rooms ?? [{ adults: 2 }],
              })
            : Promise.resolve([]),
          (filters.checkIn && filters.checkOut)
            ? getRatesForBedbankOffer({
                id: propertyId,
                rooms: filters.rooms ?? [{ adults: 2 }],
                checkIn: filters.checkIn,
                checkOut: filters.checkOut,
                region: regionCode,
                isSpoofed: false,
                source: source,
              })
            : Promise.resolve([]),
        ])
        const alternativeRates = alternativeSourceBedbankRates
          ? sortBy(
              alternativeSourceBedbankRates.filter(rate => !rate.isFlightBundle),
              selection => selection.totals.total,
              'asc',
            )
          : undefined
        const alternativeRate = alternativeRates?.[0]

        // Process original source rates
        const originalRates = originalSourceBedbankRates
          ? sortBy(
              originalSourceBedbankRates.filter(rate => !rate.isFlightBundle),
              selection => selection.totals.total,
              'asc',
            )
          : undefined
        const originalRate = originalRates?.[0]

        const createOfferMetaData = (rate: App.BedbankRate) => {
          return {
            kind: 'bedbank',
            offerId: propertyId,
            propertyId: propertyId,
            type: 'bedbank_hotel',
            distance: 0,
            available: true,
            listPosition: 0,
            rooms: [],
            packagesWithAvailableUpgrade: [],
            suggestedTravelDates: {
              checkIn: filters.checkIn ?? '',
              checkOut: filters.checkOut ?? '',
              memberPrice: rate.totals.memberTotal,
            },
            pricing: {
              lowestPrice: rate.totals.total,
              lowestMemberPrice: rate.totals.memberTotal,
              lowestPriceValue: (rate.totals.total ?? 0) - (rate.totals.propertyFees ?? 0),
              lowestPriceRoomRateId: rate.id,
              lowestPriceMobilePromotion: rate.mobilePromotion,
              duration: rate.nights,
              hasLuxPlusRate: false,
              mx: rate.mx,
            },
            cancellationPolicyInfo: {
              refundable: rate.refundable,
              partiallyRefundable: rate.partiallyRefundable,
              nonRefundableDateRanges: [],
              cancellationPolicies: rate.cancellationPolicy?.policies?.map(policy => ({
                currency: policy.currency,
                start: policy.start.toISOString(),
                end: policy.end.toISOString(),
                percent: policy.percent,
                amount: policy.amount,
                nights: policy.nights,
              })) ?? [],
            },
            availableRooms: 1,
          }
        }
        const alternativeOfferMetaData = alternativeRate
          ? createOfferMetaData(alternativeRate)
          : undefined
        const originalOfferMetaData = originalRate
          ? createOfferMetaData(originalRate)
          : undefined

        return {
          alternativeOfferMetaData,
          originalOfferMetaData,
        }
      },
    })
  }
}

export function fetchPropertyTags(propertyId: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (state.offer.propertyTags[propertyId]) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_PROPERTY_TAGS,
      propertyId,
      request: () => SearchService.getSearchTags({ propertyId }),
    })
  }
}

export function fetchOfferTags(offerId: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const tagData = state.offer.offerTags[offerId]
    // Skip if already fetched or currently fetching
    if (tagData) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_OFFER_TAGS,
      offerId,
      request: () => getTagsByOfferId({ offerId }).then(result => result.tags),
    })
  }
}

function mapPaymentOptions(paymentOptions: Record<string, number>): Record<string, number> {
  return Object.entries(paymentOptions).reduce((acc, [key, value]) => {
    if (key === 'pay_at_property') {
      acc['Pay at property'] = value
    }
    return acc
  }, {} as Record<string, number>)
}

export type MetaSearchOfferType = 'forced_rankings' | 'pinned' | 'recommended'

export function fetchMetaOfferIds(filters: App.OfferListFilters): AppAction {
  return (dispatch, getState) => {
    const offerListKey = getOfferListKey(filters)
    const state = getState()

    if (state.offer.offerLists[offerListKey] || !filters.metaSearchObject || filters.metaSearchObject.disabled) {
      return
    }

    const { metaSearchObject } = filters

    const request = () => getMetaOfferIds({
      country_code: metaSearchObject.offerCountry,
      user_region: metaSearchObject.region,
      latitude: metaSearchObject.latitude,
      longitude: metaSearchObject.longitude,
      forced_rankings_limit: metaSearchObject.limit,
      pinned_offers_limit: metaSearchObject.limit,
      offer_id: metaSearchObject.offerId,
      use_mocks: config.LERE_USE_MOCKS ? 'true' : 'false',
    }).then(response => ({
      mainOfferList: response.result.offer_ids.map(offer => offer.offer_id),
      offerCount: response.result.offer_ids.length,
      metaSearchOfferTypes: response.result.offer_ids.reduce((acc, offer) => {
        acc[offer.offer_id] = offer.type
        return acc
      }, {} as Record<string, MetaSearchOfferType>),
      metaSearchOfferDates: response.result.offer_ids.reduce((acc, offer) => {
        if (offer.check_in || offer.check_out) {
          acc[offer.offer_id] = {
            checkIn: offer.check_in,
            checkOut: offer.check_out,
          }
        }
        return acc
      }, {} as Record<string, { checkIn?: string, checkOut?: string }>),
    }))

    dispatch({
      type: API_CALL,
      api: FETCH_OFFER_LIST,
      request,
      key: offerListKey,
    })
  }
}
